import { useState, useEffect, useCallback } from 'react';
import {
  API_GET_TEAMS,
  API_GET_TEAM_DASHBOARDS,
  API_GET_TEAMS_MENUS,
  API_UPDATE_TEAMSDASHBOARDS_WHEN_TEAM_DELETE_FROM_PLATFORM
} from '../../../../api/configurationScreenServices';
import { FEATURE_KEYS } from '../../../../config/featureRegistry';
import { useFeatureListData } from '../../../../hooks/useFeatureListData';
import { hierarchyToLinear, deletedTeamsFromPlatform } from '../../../../utils/globalUtils';

/**
 * Hook to fetch and normalize Mobile Teams homepages data.
 *
 * Replaces the Redux getTeamsAndTeamsAssignments thunk:
 * - fetches team-dashboard assignments (deduped by teamId)
 * - prunes assignments for teams deleted from the platform (backend sync
 *   via API_UPDATE_TEAMSDASHBOARDS_WHEN_TEAM_DELETE_FROM_PLATFORM)
 * - fetches all homepages via the feature registry
 */
export function useMobileTeamsHomepages() {
  // Get all homepages via feature registry
  const { itemsById: homepagesById, refetch: refetchHomepages } = useFeatureListData(
    FEATURE_KEYS.HOMEPAGE
  );

  const [state, setState] = useState({
    homepages: null,
    loading: true
  });

  const fetchData = useCallback(async () => {
    try {
      const [teamsResponse, dashboardsResponse, menusResponse] = await Promise.all([
        API_GET_TEAMS(true),
        API_GET_TEAM_DASHBOARDS(),
        API_GET_TEAMS_MENUS()
      ]);

      const teamDashboards = dashboardsResponse?.data;
      if (!teamDashboards) {
        setState({
          homepages: { defaultDashboards: {}, teamsDashboards: [] },
          loading: false
        });
        return;
      }

      // Dedupe assignments by teamId (first one wins — same as the old thunk)
      const dashboardsByTeamId = {};
      for (const teamsDashboard of teamDashboards.teamsDashboards || []) {
        if (!dashboardsByTeamId[teamsDashboard.teamId]) {
          dashboardsByTeamId[teamsDashboard.teamId] = teamsDashboard;
        }
      }

      // Prune mappings of teams deleted from the platform (backend sync)
      const linearTeams = hierarchyToLinear(teamsResponse?.data);
      const deletedTeamIds = deletedTeamsFromPlatform(
        linearTeams,
        teamDashboards.teamsDashboards,
        menusResponse?.data?.teamMenu
      );
      for (const teamId of deletedTeamIds) {
        try {
          await API_UPDATE_TEAMSDASHBOARDS_WHEN_TEAM_DELETE_FROM_PLATFORM(teamId);
          delete dashboardsByTeamId[teamId];
        } catch (error) {
          console.error("Error pruning deleted team's dashboards:", error);
        }
      }
      if (deletedTeamIds.length !== 0) {
        refetchHomepages();
      }

      setState({
        homepages: {
          ...teamDashboards,
          defaultDashboards: teamDashboards.defaultDashboards || {},
          teamsDashboards: Object.values(dashboardsByTeamId)
        },
        loading: false
      });
    } catch (error) {
      console.error('Error fetching mobile teams homepages:', error);
      setState({
        homepages: { defaultDashboards: {}, teamsDashboards: [] },
        loading: false
      });
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    homepagesById,
    homepages: state.homepages,
    loading: state.loading,
    refetchHomepages,
    refetchTeamDashboards: fetchData
  };
}
